import {
  DocumentDuplicateIcon,
  DuplicateIcon,
  FolderIcon as FolderMoveIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/outline";

import { DropdownMenuVariant } from "../ui/DropdownMenu";

import { getEntitySingularLabel } from "./entity-labels";
import { RegistryItem } from "./types";

import { EntityIcon } from "@/components/ui/icons";
import { Entity, EntityType } from "@/queries/folder-entities/types";

const ENTITY_COLORS = {
  [EntityType.PROMPT]: {
    background: "bg-amber-50",
    text: "text-amber-600",
  },
  [EntityType.WORKFLOW]: {
    background: "bg-violet-50",
    text: "text-violet-600",
  },
  [EntityType.DATASET]: {
    background: "bg-indigo-50",
    text: "text-indigo-600",
  },
  [EntityType.REPORT]: {
    background: "bg-emerald-50",
    text: "text-emerald-600",
  },
  [EntityType.AB_TEST]: {
    background: "bg-pink-50",
    text: "text-pink-600",
  },
  [EntityType.FOLDER]: {
    background: "bg-gray-50",
    text: "text-gray-500",
  },
  [EntityType.SNIPPET]: {
    background: "bg-gray-50",
    text: "text-gray-600",
  },
  [EntityType.INPUT_VARIABLE_SET]: {
    background: "bg-teal-50",
    text: "text-teal-600",
  },
  [EntityType.SKILL_COLLECTION]: {
    background: "bg-sky-50",
    text: "text-sky-600",
  },
  [EntityType.SMART_TABLE]: {
    background: "bg-cyan-50",
    text: "text-cyan-600",
  },
  [EntityType.TOOL]: {
    background: "bg-fuchsia-50",
    text: "text-fuchsia-600",
  },
} as const;

// Color intensity mapping for different intensities
export const COLOR_INTENSITIES = {
  50: "50",
  100: "100",
  200: "200",
  300: "300",
  400: "400",
  500: "500",
  600: "600",
  700: "700",
  800: "800",
  900: "900",
} as const;

const getEntityName = (type: EntityType) => {
  if (type === EntityType.SMART_TABLE) {
    return "Table";
  }

  return getEntitySingularLabel(type);
};

const getItemColor = (
  type: EntityType,
  optionalColorIntensity?: keyof typeof COLOR_INTENSITIES,
): string => {
  const baseColor = (() => {
    switch (type) {
      case EntityType.PROMPT:
        return "amber";
      case EntityType.WORKFLOW:
        return "violet";
      case EntityType.DATASET:
        return "indigo";
      case EntityType.REPORT:
        return "emerald";
      case EntityType.AB_TEST:
        return "pink";
      case EntityType.FOLDER:
        return "gray";
      case EntityType.SNIPPET:
        return "gray";
      case EntityType.INPUT_VARIABLE_SET:
        return "teal";
      case EntityType.SKILL_COLLECTION:
        return "sky";
      case EntityType.SMART_TABLE:
        return "cyan";
      case EntityType.TOOL:
        return "fuchsia";
      default:
        return "gray";
    }
  })();

  const intensity = optionalColorIntensity || 50;
  return `bg-${baseColor}-${intensity}`;
};

const getItemIcon = (
  type: EntityType,
  disabled = false,
  size: "xs" | "sm" | "md" | "lg" = "sm",
  className?: string,
) => <EntityIcon type={type} disabled={disabled} size={size} className={className} />;

const getTypeIcon = (type: EntityType) => <EntityIcon type={type} />;

const getItemBodyIcon = (item: RegistryItem) => <EntityIcon type={item.type} size="lg" />;

const getItemLink = (item: RegistryItem, workspaceId: number) => {
  // eslint-disable-next-line default-case
  switch (item.type) {
    case EntityType.PROMPT:
      return item.version_number
        ? `/workspace/${workspaceId}/prompt/${item.id}/version/${item.version_number}`
        : `/workspace/${workspaceId}/prompt/${item.id}`;
    case EntityType.WORKFLOW:
      return item.version_number
        ? `/workspace/${workspaceId}/workflow/${item.id}/version/${item.version_number}`
        : `/workspace/${workspaceId}/workflow/${item.id}`;
    case EntityType.FOLDER:
      return `/workspace/${workspaceId}/folder/${item.id}`;
    case EntityType.DATASET:
      return item.version_number
        ? `/workspace/${workspaceId}/dataset-groups/${item.id}/dataset/${item.version_number}`
        : `/workspace/${workspaceId}/dataset-groups/${item.id}`;
    case EntityType.REPORT:
      return `/workspace/${workspaceId}/evaluate/blueprints/${item.id}`;
    case EntityType.AB_TEST:
      return `/workspace/${workspaceId}/ab-releases/prompt-release-group/${item.id}`;
    case EntityType.SNIPPET:
      return item.version_number
        ? `/workspace/${workspaceId}/prompt/${item.id}/version/${item.version_number}`
        : `/workspace/${workspaceId}/prompt/${item.id}`;
    case EntityType.INPUT_VARIABLE_SET:
      return `/workspace/${workspaceId}/input-variable-set/${item.id}`;
    case EntityType.SMART_TABLE:
      return `/workspace/${workspaceId}/smart-tables/${item.id}`;
    case EntityType.SKILL_COLLECTION:
      return `/workspace/${workspaceId}/skill-collection/${item.id}`;
    case EntityType.TOOL:
      return `/workspace/${workspaceId}/tool/${item.id}`;
  }
};
export interface ItemActionRbac {
  isEditDisabled: (itemType: EntityType) => boolean;
  isDeleteDisabled: (itemType: EntityType) => boolean;
  isCreateDisabled: (itemType: EntityType) => boolean;
}

const getItemActions = (
  item: RegistryItem,
  rbac: ItemActionRbac,
  dialogActions?: {
    openMoveFolder: (entity: Entity) => void;
    openRenameEntity: (entity: Entity) => void;
    deleteEntity: (entity: Entity) => Promise<void>;
    openDuplicateEntity?: (entity: Entity) => void;
    branchToWorkspace?: (prompt: Entity) => void;
  },
) => {
  const { isEditDisabled, isDeleteDisabled, isCreateDisabled } = rbac;

  const deleteAction = {
    id: "delete",
    label: "Delete",
    variant: DropdownMenuVariant.DESTRUCTIVE,
    icon: TrashIcon,
    getDisabled: () => isDeleteDisabled(item.type),
    onClick: () => {
      if (dialogActions?.deleteEntity) {
        dialogActions.deleteEntity(item);
      }
    },
    tooltipContent: isDeleteDisabled(item.type)
      ? "You don't have permission to delete this item"
      : undefined,
  };

  const moveToFolderAction = dialogActions?.openMoveFolder
    ? {
        id: "move-to-folder",
        label: "Move to folder",
        variant: DropdownMenuVariant.PRIMARY,
        icon: FolderMoveIcon,
        onClick: () => {
          dialogActions.openMoveFolder(item);
        },
        getDisabled: () => isEditDisabled(item.type),
        tooltipContent: isEditDisabled(item.type)
          ? "You don't have permission to move this item"
          : undefined,
      }
    : null;

  const renameAction = dialogActions?.openRenameEntity
    ? {
        id: "rename",
        label: "Rename",
        variant: DropdownMenuVariant.PRIMARY,
        icon: PencilIcon,
        onClick: () => {
          dialogActions.openRenameEntity(item);
        },
        getDisabled: () => isEditDisabled(item.type),
        tooltipContent: isEditDisabled(item.type)
          ? "You don't have permission to rename this item"
          : undefined,
      }
    : {
        id: "rename",
        label: "Rename",
        variant: DropdownMenuVariant.PRIMARY,
        icon: PencilIcon,
        onClick: () => undefined,
        getDisabled: () => isEditDisabled(item.type),
        tooltipContent: isEditDisabled(item.type)
          ? "You don't have permission to rename this item"
          : undefined,
      };

  const duplicateDisabled = isCreateDisabled(item.type);
  const duplicateTooltip = duplicateDisabled
    ? "You don't have permission to duplicate this item"
    : undefined;

  // eslint-disable-next-line default-case
  switch (item.type) {
    case EntityType.PROMPT:
      return [
        ...(moveToFolderAction ? [moveToFolderAction] : []),
        {
          id: "duplicate",
          label: "Duplicate",
          variant: DropdownMenuVariant.PRIMARY,
          icon: DocumentDuplicateIcon,
          getDisabled: () => duplicateDisabled,
          tooltipContent: duplicateTooltip,
          onClick: () => {
            if (dialogActions?.openDuplicateEntity) {
              dialogActions.openDuplicateEntity(item);
            }
          },
        },
        {
          id: "copy-version",
          label: "Copy a version",
          variant: DropdownMenuVariant.PRIMARY,
          icon: DuplicateIcon,
          getDisabled: () => duplicateDisabled,
          tooltipContent: duplicateTooltip,
          onClick: () => {
            if (dialogActions?.branchToWorkspace) {
              dialogActions.branchToWorkspace(item);
            }
          },
        },
        ...(renameAction ? [renameAction] : []),
        deleteAction,
      ];
    case EntityType.SNIPPET:
      return [
        ...(moveToFolderAction ? [moveToFolderAction] : []),
        {
          id: "duplicate",
          label: "Duplicate",
          variant: DropdownMenuVariant.PRIMARY,
          icon: DocumentDuplicateIcon,
          getDisabled: () => duplicateDisabled,
          tooltipContent: duplicateTooltip,
          onClick: () => {
            if (dialogActions?.openDuplicateEntity) {
              dialogActions.openDuplicateEntity(item);
            }
          },
        },
        {
          id: "copy-version",
          label: "Copy a version",
          variant: DropdownMenuVariant.PRIMARY,
          icon: DuplicateIcon,
          getDisabled: () => duplicateDisabled,
          tooltipContent: duplicateTooltip,
          onClick: () => {
            if (dialogActions?.branchToWorkspace) {
              dialogActions.branchToWorkspace(item);
            }
          },
        },
        ...(renameAction ? [renameAction] : []),
        deleteAction,
      ];
    case EntityType.WORKFLOW:
      return [
        ...(moveToFolderAction ? [moveToFolderAction] : []),
        {
          id: "duplicate",
          label: "Duplicate",
          variant: DropdownMenuVariant.PRIMARY,
          icon: DocumentDuplicateIcon,
          getDisabled: () => duplicateDisabled,
          tooltipContent: duplicateTooltip,
          onClick: () => {
            if (dialogActions?.openDuplicateEntity) {
              dialogActions.openDuplicateEntity(item);
            }
          },
        },
        ...(renameAction ? [renameAction] : []),
        deleteAction,
      ];
    case EntityType.FOLDER:
      return [
        ...(moveToFolderAction ? [moveToFolderAction] : []),
        ...(renameAction ? [renameAction] : []),
        deleteAction,
      ];
    case EntityType.DATASET:
      return [
        ...(moveToFolderAction ? [moveToFolderAction] : []),
        ...(renameAction ? [renameAction] : []),
        deleteAction,
      ];
    case EntityType.REPORT:
      return [
        ...(moveToFolderAction ? [moveToFolderAction] : []),
        {
          id: "duplicate",
          label: "Duplicate",
          variant: DropdownMenuVariant.PRIMARY,
          icon: DocumentDuplicateIcon,
          getDisabled: () => duplicateDisabled,
          tooltipContent: duplicateTooltip,
          onClick: () => {
            if (dialogActions?.openDuplicateEntity) {
              dialogActions.openDuplicateEntity(item);
            }
          },
        },
        ...(renameAction ? [renameAction] : []),
        deleteAction,
      ];
    case EntityType.AB_TEST:
      return [
        ...(moveToFolderAction ? [moveToFolderAction] : []),
        {
          id: "duplicate",
          label: "Duplicate",
          variant: DropdownMenuVariant.PRIMARY,
          icon: DocumentDuplicateIcon,
          getDisabled: () => duplicateDisabled,
          tooltipContent: duplicateTooltip,
          onClick: () => undefined,
        },
        deleteAction,
      ];
    case EntityType.INPUT_VARIABLE_SET:
      return [
        ...(moveToFolderAction ? [moveToFolderAction] : []),
        ...(renameAction ? [renameAction] : []),
        deleteAction,
      ];
    case EntityType.SKILL_COLLECTION:
      return [...(renameAction ? [renameAction] : []), deleteAction];
    case EntityType.SMART_TABLE:
      return [
        ...(moveToFolderAction ? [moveToFolderAction] : []),
        {
          id: "duplicate",
          label: "Duplicate",
          variant: DropdownMenuVariant.PRIMARY,
          icon: DocumentDuplicateIcon,
          getDisabled: () => duplicateDisabled,
          tooltipContent: duplicateTooltip,
          onClick: () => {
            if (dialogActions?.openDuplicateEntity) {
              dialogActions.openDuplicateEntity(item);
            }
          },
        },
        ...(renameAction ? [renameAction] : []),
        deleteAction,
      ];
    case EntityType.TOOL:
      return [
        ...(moveToFolderAction ? [moveToFolderAction] : []),
        {
          id: "duplicate",
          label: "Duplicate",
          variant: DropdownMenuVariant.PRIMARY,
          icon: DocumentDuplicateIcon,
          getDisabled: () => duplicateDisabled,
          tooltipContent: duplicateTooltip,
          onClick: () => {
            if (dialogActions?.openDuplicateEntity) {
              dialogActions.openDuplicateEntity(item);
            }
          },
        },
        ...(renameAction ? [renameAction] : []),
        deleteAction,
      ];
  }
};

const getEntityColor = (type: EntityType): string =>
  ENTITY_COLORS[type]?.background || "bg-gray-50";

const getEntityIconColor = (type: EntityType): string =>
  ENTITY_COLORS[type]?.text || "text-gray-500";

export {
  ENTITY_COLORS,
  getEntityColor,
  getEntityIconColor,
  getEntityName,
  getItemActions,
  getItemBodyIcon,
  getItemColor,
  getItemIcon,
  getItemLink,
  getTypeIcon,
};
